import { useEffect, useState } from 'react';
import useGlobalFilters from '../../../hooks/useGlobalFilters';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import {
    useDeregisterAvisoObraMutation,
    useDownloadAvisoObraPdfMutation,
    useGenerateNoObraPdfMutation,
    useGetAvisosObraQuery,
    useGetTiposObraQuery,
    useLazyGetAvisosObraQuery,
    useRevisionAvisoObraMutation,
    useUpdateAvisoObraMutation
} from '../../../services/avisoObraApi';
import { useNavigate } from 'react-router-dom';
import { ACTION_TYPES } from '../consts/avisoObra.types';
import { EstadoAvisoObraEnum } from '../consts/avisoObraStatesTypes';
import { useSnackbar } from 'notistack';
import { downloadFromBase64 } from '../../../utils/common';
import { hideAlert, showAlert } from '../../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../../constants/values';
import { useAppDispatch } from '../../../hooks/reduxHooks';

export const useAvisoObra = (filters: any) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { extraFilters, setFilters, handleChangeExtraFilters, handleSubmitSearch, clearFilters } =
        useGlobalFilters();
    const { enqueueSnackbar } = useSnackbar();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedItem, setSelectedItem] = useState<any>(null);
    const [updateAvisoObra] = useUpdateAvisoObraMutation();
    const [revisionAvisoObra] = useRevisionAvisoObraMutation();
    const [deregisterAvisoObra] = useDeregisterAvisoObraMutation();
    const [generateNoObraPdf] = useGenerateNoObraPdfMutation();
    const [downloadAvisoObraPdf, { isLoading: isDownloadingPdf }] =
        useDownloadAvisoObraPdfMutation();

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, item: any) => {
        setAnchorEl(event.currentTarget);
        setSelectedItem(item);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setSelectedItem(null);
    };

    const handleChangeEstado = async (idAviso: number, estado: EstadoAvisoObraEnum) => {
        try {
            handleCloseMenu();

            await updateAvisoObra({
                idAvisoObra: idAviso,
                body: { estado }
            }).unwrap();

            enqueueSnackbar('Estado actualizado correctamente.', {
                variant: 'success'
            });

            fetchAvisosObra(filters);
        } catch (error: any) {
            dispatch(
                showAlert({
                    title: 'No se pudo cambiar el estado del aviso de obra',
                    message: error?.data?.message || ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const handleRevisionAvisoObra = async (idAviso: number) => {
        try {
            handleCloseMenu();

            await revisionAvisoObra(idAviso).unwrap();

            enqueueSnackbar('Aviso de obra enviado a revisión correctamente.', {
                variant: 'success'
            });

            fetchAvisosObra(filters);
        } catch (error: any) {
            dispatch(
                showAlert({
                    title: 'No se pudo enviar el aviso de obra a revisión',
                    message: error?.data?.message || ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const handleCancelarAvisoObra = async (idAviso: number) => {
        try {
            handleCloseMenu();

            await deregisterAvisoObra({
                avisoDeObraID: idAviso,
                body: {
                    observacion: 'Cancelado por el usuario desde la interfaz'
                }
            }).unwrap();

            enqueueSnackbar('Aviso de obra cancelado correctamente.', {
                variant: 'success'
            });

            fetchAvisosObra(filters);
        } catch (error: any) {
            dispatch(
                showAlert({
                    title: 'No se pudo cancelar el aviso de obra',
                    message: error?.data?.message || ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const handleEditar = () => {
        if (selectedItem) {
            handleCloseMenu();
            navigate('./editar-aviso-de-obra', {
                state: {
                    ...selectedItem,
                    isEdit: true
                }
            });
        }
    };

    const handleVer = () => {
        if (selectedItem) {
            handleCloseMenu();
            navigate('./visualizar', {
                state: {
                    ...selectedItem,
                    isView: true,
                    isEdit: true
                }
            });
        }
    };

    const handleDescargar = async () => {
        if (!selectedItem) return;
        handleCloseMenu();
        try {
            const response = await downloadAvisoObraPdf(selectedItem.id).unwrap();
            downloadFromBase64(response.data, selectedItem.id, 'aviso-de-obra');
        } catch (error: any) {
            dispatch(
                showAlert({
                    title: 'No se pudo descargar el PDF del aviso de obra',
                    message: error?.data?.message || ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };
    const handleGenerarNoObraPdf = (idAviso: number) => {
        handleCloseMenu();

        dispatch(
            showAlert({
                title: 'Confirmar generación de Nota No Construcción',
                message: 'Se generará el PDF de Nota No Construcción (NNC). ',
                icon: 'info',
                confirmText: 'Generar NNC',
                cancelText: 'Cancelar',
                confirmAction: async () => {
                    try {
                        const response = await generateNoObraPdf(idAviso).unwrap();

                        downloadFromBase64(response.data, idAviso, 'nota-no-construccion');

                        enqueueSnackbar('PDF generado correctamente.', { variant: 'success' });

                        dispatch(hideAlert());
                        fetchAvisosObra(filters);
                    } catch (error: any) {
                        dispatch(
                            showAlert({
                                title: 'Error al generar PDF',
                                message:
                                    error?.data?.message ||
                                    'Error al generar la Nota No Construcción.',
                                icon: 'error',
                                confirmText: 'Aceptar',
                                confirmAction: () => dispatch(hideAlert())
                            })
                        );
                    }
                },
                cancelAction: () => {
                    dispatch(hideAlert());
                }
            })
        );
    };
    const handleSuspenderActionClick = (
        setDrawerOpen: any,
        setDrawerAction: any,
        setSelectedObra: any
    ) => {
        handleCloseMenu();
        setDrawerAction(ACTION_TYPES.SUSPEND);
        setSelectedObra(selectedItem);
        setDrawerOpen(true);
    };

    const handleExtenderActionClick = (
        setDrawerOpen: any,
        setDrawerAction: any,
        setSelectedObra: any
    ) => {
        handleCloseMenu();
        setDrawerAction(ACTION_TYPES.EXTEND);
        setSelectedObra(selectedItem);
        setDrawerOpen(true);
    };

    const handleUploadProgramaActionClick = (
        setDrawerOpen: any,
        setDrawerAction: any,
        setSelectedObra: any
    ) => {
        handleCloseMenu();
        setDrawerAction(ACTION_TYPES.UPLOAD_PDF_PROGRAMA);
        setSelectedObra(selectedItem);
        setDrawerOpen(true);
    };

    const handleRecepcionProgramaActionClick = (
        setDrawerOpen: any,
        setDrawerAction: any,
        setSelectedObra: any
    ) => {
        handleCloseMenu();
        setDrawerAction(ACTION_TYPES.RECEIVE_PROGRAMA);
        setSelectedObra(selectedItem);
        setDrawerOpen(true);
    };

    const handleAprobacionProgramaActionClick = (
        setDrawerOpen: any,
        setDrawerAction: any,
        setSelectedObra: any
    ) => {
        handleCloseMenu();
        setDrawerAction(ACTION_TYPES.APPROVE_PROGRAMA);
        setSelectedObra(selectedItem);
        setDrawerOpen(true);
    };

    const handleReiniciarActionClick = (
        setDrawerOpen: any,
        setDrawerAction: any,
        setSelectedObra: any
    ) => {
        handleCloseMenu();
        setDrawerAction(ACTION_TYPES.RESTART);
        setSelectedObra(selectedItem);
        setDrawerOpen(true);
    };

    const handleDeregisterActionClick = (
        setDrawerOpen: any,
        setDrawerAction: any,
        setSelectedObra: any
    ) => {
        handleCloseMenu();
        setDrawerAction(ACTION_TYPES.DEREGISTER);
        setSelectedObra(selectedItem);
        setDrawerOpen(true);
    };

    const handleConfirmDeregisterActionClick = (
        setDrawerOpen: any,
        setDrawerAction: any,
        setSelectedObra: any
    ) => {
        handleCloseMenu();
        setDrawerAction(ACTION_TYPES.CONFIRM_DEREGISTER);
        setSelectedObra(selectedItem);
        setDrawerOpen(true);
    };

    const [
        fetchAvisosObra,
        { data: avisosDataRaw, isFetching: isFetchingList, isError: isErrorList, error: errorList }
    ] = useLazyGetAvisosObraQuery();

    const {
        data: tiposObraData,
        isFetching: isFetchingTipos,
        isError: isErrorTipos,
        error: errorTipos
    } = useGetTiposObraQuery();

    useEffect(() => {
        fetchAvisosObra(filters);
    }, [filters]);

    const avisos = {
        data: avisosDataRaw ?? []
    };

    const contratoOrganismoCentral = useSelector((state: RootState) => {
        const organismos = state?.auth?.user?.organismos ?? [];
        const organismoConContrato = organismos.find(
            (org) => org?.idOrganismoCentral !== null && org?.idOrganismoCentral !== undefined
        );
        return organismoConContrato?.idOrganismoCentral ?? null;
    });

    return {
        avisos,
        isFetching: isFetchingList,
        filters,
        extraFilters,
        anchorEl,
        selectedItem,
        handleOpenMenu,
        handleCloseMenu,
        handleEditar,
        handleVer,
        handleDescargar,
        handleSuspenderActionClick,
        handleExtenderActionClick,
        handleReiniciarActionClick,
        handleDeregisterActionClick,
        setFilters,
        handleChangeExtraFilters,
        handleSubmitSearch,
        clearFilters,
        handleRecepcionProgramaActionClick,
        contratoOrganismoCentral,
        tiposObraData,
        isFetchingTipos,
        isErrorList,
        errorList,
        isErrorTipos,
        errorTipos,
        handleChangeEstado,
        handleUploadProgramaActionClick,
        handleRevisionAvisoObra,
        handleCancelarAvisoObra,
        handleAprobacionProgramaActionClick,
        handleConfirmDeregisterActionClick,
        isDownloadingPdf,
        handleGenerarNoObraPdf,
        fetchAvisosObra
    };
};
